import { Request, Response } from 'express';
import { injectable, inject } from 'inversify';
import { PackageMappingApplicationService } from '../../application/PackageMappingApplicationService';
import { 
    CreatePackageMappingRequestSchema, 
    UpdatePackageMappingRequestSchema,
    BulkPackageMappingRequestSchema,
    GetPackageMappingsQuerySchema 
} from '../dto/PackageMappingRequestDTOs';
import { validateRequest, createErrorResponse } from '../middleware/ValidationMiddleware';
import { 
  createSuccessResponse, 
  createErrorResponse as createStandardErrorResponse, 
  BUSINESS_ACTIONS, 
  DOMAIN_NAMES 
} from '@swoft/core';

/**
 * Package Mapping Controller
 * 
 * RESTful API controller for package-to-domain mapping operations.
 * Enables intelligent package classification and domain alignment.
 * 
 * Enforces Clean Architecture by maintaining strict API boundaries.
 */
@injectable()
export class PackageMappingController {
    constructor(
        @inject('PackageMappingApplicationService')
        private readonly packageMappingApplicationService: PackageMappingApplicationService
    ) {}

    /**
     * Get package mappings with optional filtering
     * GET /api/package-mappings
     */
    async getPackageMappings(req: Request, res: Response): Promise<void> {
        try {
            // Validate query parameters
            const validatedQuery = GetPackageMappingsQuerySchema.parse(req.query);
            
            // Execute application service
            const result = await this.packageMappingApplicationService.getPackageMappings(validatedQuery);
            
            const response = createSuccessResponse(
                {
                    mappings: result.mappings,
                    total: result.total
                },
                BUSINESS_ACTIONS.DDD.LIST_PACKAGE_MAPPINGS,
                DOMAIN_NAMES.DDD,
                'query'
            );
            
            res.json(response);
        } catch (error: any) {
            const errorResponse = createStandardErrorResponse(
                'PACKAGE_MAPPING_LIST_FAILED',
                'Failed to retrieve package mappings',
                BUSINESS_ACTIONS.DDD.LIST_PACKAGE_MAPPINGS,
                DOMAIN_NAMES.DDD,
                'query',
                error instanceof Error ? error.message : 'Unknown error'
            );
            res.status(400).json(errorResponse);
        }
    }

    /**
     * Get specific package mapping
     * GET /api/package-mappings/:packageName
     */
    async getPackageMapping(req: Request, res: Response): Promise<void> {
        try {
            const packageName = decodeURIComponent(req.params.packageName);
            
            // Execute application service
            const mapping = await this.packageMappingApplicationService.getPackageMapping(packageName);
            
            if (!mapping) {
                const errorResponse = createStandardErrorResponse(
                    'PACKAGE_MAPPING_NOT_FOUND',
                    `Package mapping not found: ${packageName}`,
                    BUSINESS_ACTIONS.DDD.LIST_PACKAGE_MAPPINGS,
                    DOMAIN_NAMES.DDD,
                    'query'
                );
                res.status(404).json(errorResponse);
                return;
            }
            
            const response = createSuccessResponse(
                mapping,
                BUSINESS_ACTIONS.DDD.LIST_PACKAGE_MAPPINGS,
                DOMAIN_NAMES.DDD,
                'query'
            );
            
            res.json(response);
        } catch (error: any) {
            const statusCode = error.message?.includes('not found') ? 404 : 400;
            const errorResponse = createStandardErrorResponse(
                'PACKAGE_MAPPING_GET_FAILED',
                'Failed to retrieve package mapping',
                BUSINESS_ACTIONS.DDD.LIST_PACKAGE_MAPPINGS,
                DOMAIN_NAMES.DDD,
                'query',
                error instanceof Error ? error.message : 'Unknown error'
            );
            res.status(statusCode).json(errorResponse);
        }
    }

    /**
     * Create a new package mapping
     * POST /api/package-mappings
     */
    async createPackageMapping(req: Request, res: Response): Promise<void> {
        try {
            // Validate request body
            const validatedRequest = CreatePackageMappingRequestSchema.parse(req.body);
            
            // Execute application service
            const mapping = await this.packageMappingApplicationService.createPackageMapping(validatedRequest);
            
            const response = createSuccessResponse(
                mapping,
                BUSINESS_ACTIONS.DDD.CREATE_PACKAGE_MAPPING,
                DOMAIN_NAMES.DDD,
                'command'
            );
            
            res.status(201).json(response);
        } catch (error: any) {
            const errorResponse = createStandardErrorResponse(
                'PACKAGE_MAPPING_CREATE_FAILED',
                'Failed to create package mapping',
                BUSINESS_ACTIONS.DDD.CREATE_PACKAGE_MAPPING,
                DOMAIN_NAMES.DDD,
                'command',
                error instanceof Error ? error.message : 'Unknown error'
            );
            res.status(400).json(errorResponse);
        }
    }

    /**
     * Update an existing package mapping
     * PUT /api/package-mappings/:packageName
     */
    async updatePackageMapping(req: Request, res: Response): Promise<void> {
        try {
            const packageName = decodeURIComponent(req.params.packageName);
            
            // Validate request body
            const validatedRequest = UpdatePackageMappingRequestSchema.parse(req.body);
            
            // Execute application service
            const mapping = await this.packageMappingApplicationService.updatePackageMapping(
                packageName, 
                validatedRequest
            );
            
            const response = createSuccessResponse(
                mapping,
                BUSINESS_ACTIONS.DDD.UPDATE_PACKAGE_MAPPING,
                DOMAIN_NAMES.DDD,
                'command'
            );
            
            res.json(response);
        } catch (error: any) {
            const statusCode = error.message?.includes('not found') ? 404 : 400;
            const errorResponse = createStandardErrorResponse(
                'PACKAGE_MAPPING_UPDATE_FAILED',
                'Failed to update package mapping',
                BUSINESS_ACTIONS.DDD.UPDATE_PACKAGE_MAPPING,
                DOMAIN_NAMES.DDD,
                'command',
                error instanceof Error ? error.message : 'Unknown error'
            );
            res.status(statusCode).json(errorResponse);
        }
    }

    /**
     * Bulk update package mappings
     * POST /api/package-mappings/bulk
     */
    async bulkUpdatePackageMappings(req: Request, res: Response): Promise<void> {
        try {
            // Validate request body
            const validatedRequest = BulkPackageMappingRequestSchema.parse(req.body);
            
            // Execute application service
            const result = await this.packageMappingApplicationService.bulkUpdatePackageMappings(validatedRequest);
            
            const response = createSuccessResponse(
                {
                    ...result,
                    message: `Bulk update completed: ${result.updated} packages updated`
                },
                BUSINESS_ACTIONS.DDD.UPDATE_PACKAGE_MAPPING,
                DOMAIN_NAMES.DDD,
                'command'
            );
            
            res.json(response);
        } catch (error: any) {
            const errorResponse = createStandardErrorResponse(
                'PACKAGE_MAPPING_BULK_UPDATE_FAILED',
                'Failed to bulk update package mappings',
                BUSINESS_ACTIONS.DDD.UPDATE_PACKAGE_MAPPING,
                DOMAIN_NAMES.DDD,
                'command',
                error instanceof Error ? error.message : 'Unknown error'
            );
            res.status(400).json(errorResponse);
        }
    }

    /**
     * Analyze package for domain suggestions
     * POST /api/package-mappings/analyze/:packageName
     */
    async analyzePackage(req: Request, res: Response): Promise<void> {
        try {
            const packageName = decodeURIComponent(req.params.packageName);
            
            // Execute application service
            const analysis = await this.packageMappingApplicationService.analyzePackage(packageName);
            
            const response = createSuccessResponse(
                analysis,
                BUSINESS_ACTIONS.DDD.ANALYZE_PACKAGE,
                DOMAIN_NAMES.DDD,
                'query'
            );
            
            res.json(response);
        } catch (error: any) {
            const errorResponse = createStandardErrorResponse(
                'PACKAGE_ANALYSIS_FAILED',
                'Failed to analyze package',
                BUSINESS_ACTIONS.DDD.ANALYZE_PACKAGE,
                DOMAIN_NAMES.DDD,
                'query',
                error instanceof Error ? error.message : 'Unknown error'
            );
            res.status(400).json(errorResponse);
        }
    }

    /**
     * Delete a package mapping
     * DELETE /api/package-mappings/:packageName
     */
    async deletePackageMapping(req: Request, res: Response): Promise<void> {
        try {
            const packageName = decodeURIComponent(req.params.packageName);
            
            // Execute application service
            await this.packageMappingApplicationService.deletePackageMapping(packageName);
            
            const response = createSuccessResponse(
                { message: `Package mapping deleted successfully: ${packageName}` },
                BUSINESS_ACTIONS.DDD.DELETE_PACKAGE_MAPPING,
                DOMAIN_NAMES.DDD,
                'command'
            );
            
            res.json(response);
        } catch (error: any) {
            const statusCode = error.message?.includes('not found') ? 404 : 400;
            const errorResponse = createStandardErrorResponse(
                'PACKAGE_MAPPING_DELETE_FAILED',
                'Failed to delete package mapping',
                BUSINESS_ACTIONS.DDD.DELETE_PACKAGE_MAPPING,
                DOMAIN_NAMES.DDD,
                'command',
                error instanceof Error ? error.message : 'Unknown error'
            );
            res.status(statusCode).json(errorResponse);
        }
    }

    /**
     * Get package mapping statistics
     * GET /api/package-mappings/statistics
     */
    async getPackageMappingStatistics(req: Request, res: Response): Promise<void> {
        try {
            // Execute application service
            const statistics = await this.packageMappingApplicationService.getStatistics();
            
            const response = createSuccessResponse(
                statistics,
                BUSINESS_ACTIONS.DDD.GET_PACKAGE_MAPPING_STATISTICS,
                DOMAIN_NAMES.DDD,
                'query'
            );
            
            res.json(response);
        } catch (error: any) {
            const errorResponse = createStandardErrorResponse(
                'PACKAGE_MAPPING_STATISTICS_FAILED',
                'Failed to retrieve package mapping statistics',
                BUSINESS_ACTIONS.DDD.GET_PACKAGE_MAPPING_STATISTICS,
                DOMAIN_NAMES.DDD,
                'query',
                error instanceof Error ? error.message : 'Unknown error'
            );
            res.status(400).json(errorResponse);
        }
    }
}